import {ANON_ROLE_UID} from "@buildwithflux/constants";
import {
    EnterpriseUid,
    OrganizationUid,
    parseUserUidFromPrefixed,
    toPrefixedUserUid,
    UserUid,
} from "@buildwithflux/identifiers-schema/legacy";
import {
    AccessListIdentifier,
    DocumentOwnershipForAccessList,
    DocumentPermissionsData,
    EnterpriseRole,
    EnterpriseRoleAccessListIdentifier,
    OrganizationMember,
    OrganizationRole,
    OrganizationRoleAccessListIdentifier,
    PermissionType,
    UserAccessListIdentifier,
} from "@buildwithflux/permissions-schema";

/**
 * Gets an organization role access identifier for a specific organization and role
 */
// eslint-disable-next-line no-restricted-syntax
export function getOrganizationRoleAccessListIdentifier(
    organizationUid: OrganizationUid,
    organizationRole: OrganizationRole,
): OrganizationRoleAccessListIdentifier {
    return `${organizationUid}_${organizationRole}` as const;
}

/**
 * Gets an enterprise role access identifier for a specific enterprise and role
 */
// eslint-disable-next-line no-restricted-syntax
export function getEnterpriseRoleAccessListIdentifier(
    enterpriseUid: EnterpriseUid,
    enterpriseRole: EnterpriseRole,
): EnterpriseRoleAccessListIdentifier {
    return `${enterpriseUid}_${enterpriseRole}` as const;
}

/**
 * Gets a user access identifier for a specific user
 */
// eslint-disable-next-line no-restricted-syntax
export function getUserAccessListIdentifier(userUid: UserUid): UserAccessListIdentifier {
    return toPrefixedUserUid(userUid);
}

/**
 * Given a document, with roles, generate a list of users who have at least view access.
 *
 * Categorically: this is the **anamorphism** (unfold) of the access-list algebra —
 * source-of-truth changes (document ownership + roles) are unfolded into the
 * denormalized `user_access_list` carrier.
 */
// eslint-disable-next-line no-restricted-syntax
export function generateUserAccessListForDocument(
    document: DocumentOwnershipForAccessList,
    roles: DocumentPermissionsData[],
): AccessListIdentifier[] {
    const userAccessList = roles
        .filter((role) => role.permission_type !== PermissionType.none && role.permission !== "withdrawn")
        .flatMap((role): AccessListIdentifier[] => {
            if (role.account_type === "organization") {
                // Org-type roles grant access to org members — include both owner and member identifiers
                const orgUid = role.account_uid as OrganizationUid;
                return [
                    getOrganizationRoleAccessListIdentifier(orgUid, "owner"),
                    getOrganizationRoleAccessListIdentifier(orgUid, "member"),
                ];
            }
            // User-type roles grant access to a specific user. Emit both the bare and
            // prefixed forms regardless of how the role doc stored it, so every reader
            // matches independent of which form the source happened to use.
            return [parseUserUidFromPrefixed(role.user_uid), getUserAccessListIdentifier(role.user_uid)];
        });

    if (document.owner_uid) {
        userAccessList.push(
            parseUserUidFromPrefixed(document.owner_uid),
            getUserAccessListIdentifier(document.owner_uid),
        );
    }

    if (document.organization_owner_uid) {
        // Organization owner is in the access list
        userAccessList.push(getOrganizationRoleAccessListIdentifier(document.organization_owner_uid, "owner"));

        if (document.organization_member_permission_type !== PermissionType.none) {
            // Organization members are in the access list
            userAccessList.push(getOrganizationRoleAccessListIdentifier(document.organization_owner_uid, "member"));
        }
    }

    if (document.enterprise_owner_uid) {
        // Enterprise owner is in the access list
        userAccessList.push(getEnterpriseRoleAccessListIdentifier(document.enterprise_owner_uid, "owner"));

        if (document.enterprise_member_permission_type !== PermissionType.none) {
            // Enterprise members are in the access list
            userAccessList.push(getEnterpriseRoleAccessListIdentifier(document.enterprise_owner_uid, "member"));
        }
    }

    return userAccessList;
}

/**
 * Generates a user access list for the querying side
 *
 * This is the list of identifiers that the current user (if there is one) has visibility into. If one or more of these
 * identifiers appears in the corresponding user_access_list of a document (produced by generateUserAccessListForDocument),
 * then the user has at least read access to that document.
 *
 * Categorically: this is the **catamorphism** (fold) of the access-list algebra — a
 * principal + memberships are folded into the set of identifiers that grant the
 * principal visibility.
 */
// eslint-disable-next-line no-restricted-syntax
export function generateUserAccessListForQuery(
    userUid: UserUid | undefined,
    organizationMemberships: OrganizationMember[],
): AccessListIdentifier[] {
    // The anonymous user only has access as the anonymous role
    if (!userUid) {
        return [ANON_ROLE_UID];
    }

    // Otherwise access may come via a specific user role, or document ownership, both of which are denoted with a
    // userUid. Emit both the bare and prefixed forms (mirroring generateUserAccessListForDocument) so the query
    // still matches a stale stored access list that only carries one form — e.g. a prefixed-stored doc whose array
    // predates the symmetric emission above and so lacks the bare form.
    const forQuery = [ANON_ROLE_UID, parseUserUidFromPrefixed(userUid), getUserAccessListIdentifier(userUid)];

    // Or, access may come via the organizations of which the user is a member
    for (const membership of organizationMemberships) {
        forQuery.push(getOrganizationRoleAccessListIdentifier(membership.organizationUid, membership.role));
    }

    return forQuery;
}

/**
 * Formats an access-list as a Typesense filter clause.
 *
 * This is the canonical carrier interpreter for the Typesense search backend.
 * It replaces the ad-hoc `PartLibrarySearchFilters.anyUsersHaveAccess(uids, "typesense")`
 * and equivalent hand-rolled helpers in calling services.
 */
// eslint-disable-next-line no-restricted-syntax
export function toTypesenseAccessFilter(accessList: AccessListIdentifier[]): string {
    return `userAccessList:=[${accessList.join(",")}]`;
}

/**
 * Decides whether a principal appears in a document's stored access list.
 *
 * Categorically: this is where the two halves of the access-list algebra meet. The
 * carrier unfolded by `generateUserAccessListForDocument` (stored as `user_access_list`
 * on a Firestore document, `userAccessList` on a search document) is intersected with
 * the identifiers folded out of the principal by `generateUserAccessListForQuery`. A
 * non-empty intersection means at least view access.
 *
 * This is the in-memory counterpart of `toTypesenseAccessFilter`: same decision, taken
 * against an access list already in hand instead of pushed down to a search backend.
 * Readers go through it rather than intersecting by hand, so identifier-form handling
 * (bare vs prefixed user uids, org and enterprise role identifiers) stays in one place.
 *
 * `storedAccessList` is typed as `readonly string[]` because search documents carry the
 * list untyped; identifiers that are not `AccessListIdentifier`s simply never match.
 */
// eslint-disable-next-line no-restricted-syntax
export function accessListGrantsAccess(
    storedAccessList: readonly string[] | undefined,
    userUid: UserUid | undefined,
    organizationMemberships: OrganizationMember[],
): boolean {
    if (!storedAccessList || storedAccessList.length === 0) {
        return false;
    }

    const forQuery = new Set<string>(generateUserAccessListForQuery(userUid, organizationMemberships));

    return storedAccessList.some((identifier) => forQuery.has(identifier));
}
